// services/AdminService.js - Optimized version with request deduplication
import apiService from './api';
import axios from 'axios';

// Create direct axios instance for fallback
const _adminBaseURL = process.env.REACT_APP_API_URL || ``;
console.log(`AdminService.js using API base URL: ${_adminBaseURL}`);
const directAxios = axios.create({
  baseURL: _adminBaseURL, // Adjusted default base URL
});

class AdminService {
  constructor() {
    // Request deduplication cache
    this.requestCache = new Map();
    this.cacheTimeout = 2000; // 2 seconds cache
    
    // Pending requests to avoid duplicate API calls
    this.pendingRequests = new Map();
  }

  // Helper method to create cache key
  createCacheKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    return `${endpoint}:${JSON.stringify(sortedParams)}`;
  }

  // Helper method to check cache
  getCachedResult(cacheKey) {
    const cached = this.requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      console.log(`Using cached result for: ${cacheKey}`);
      return cached.data;
    }
    return null;
  }

  // Helper method to cache result
  setCacheResult(cacheKey, data) {
    this.requestCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    // Clean up old cache entries
    setTimeout(() => {
      this.requestCache.delete(cacheKey);
    }, this.cacheTimeout * 2);
  }

  // Deduplicated API call wrapper
  async makeRequest(endpoint, params = {}, options = {}) {
    const cacheKey = this.createCacheKey(endpoint, params);
    
    // Check cache first
    const cached = this.getCachedResult(cacheKey);
    if (cached) {
      return cached;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      console.log(`Waiting for pending request: ${cacheKey}`);
      return await this.pendingRequests.get(cacheKey);
    }

    // Make the request
    const requestPromise = this.executeRequest(endpoint, params, options);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      this.setCacheResult(cacheKey, result);
      return result;
    } finally {
      this.pendingRequests.delete(cacheKey);
    }
  }

  async executeRequest(endpoint, params, options) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;
    return await apiService.get(url, options);
  }
  // Get audit logs with deduplication and proper sorting
  async getAuditLogs(filters = {}) {
    try {
      // Make sure page is always at least 1 (1-based pagination)
      const params = {
        page: Math.max(1, filters.page || 1),
        limit: filters.limit || 25,
        sort_by: 'timestamp',
        sort_order: 'desc', // Latest first
        ...filters
      };

      console.log('Fetching audit logs with params:', params);
      
      const response = await this.makeRequest('/api/admin/audit-logs', params);
      console.log('Audit logs API response:', {
        hasData: !!response.data,
        hasLogs: !!response.data?.logs,
        logsCount: response.data?.logs?.length || 0,
        total: response.data?.total || 0,
        page: response.data?.page || 1
      });

      if (!response.data) {
        return { logs: [], total: 0, page: params.page };
      }

      if (Array.isArray(response.data)) {
        return { logs: response.data, total: response.data.length, page: params.page };
      }

      return {
        logs: response.data.logs || [],
        total: response.data.total || response.data.count || (response.data.logs ? response.data.logs.length : 0),
        page: response.data.page || params.page,
        limit: response.data.limit || params.limit
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error.message);
      throw error;
    }
  }

  // Get dashboard statistics with caching
  async getDashboardStats() {
    try {
      const response = await this.makeRequest('/api/admin/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return null;
    }
  }

  // Get query logs with deduplication
  async getQueryLogs(filters = {}) {
    try {
      const params = {
        page: filters.page || 1,
        limit: filters.limit || 100,
        ...filters
      };

      const response = await this.makeRequest('/api/admin/query-logs', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching query logs:', error);
      throw error;
    }
  }

  // Get feedback entries with deduplication
  async getFeedbackEntries(filters = {}) {
    try {
      const params = {
        page: filters.page || 1,
        limit: filters.limit || 100,
        ...filters
      };

      const response = await this.makeRequest('/api/admin/feedback-entries', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching feedback entries:', error);
      throw error;
    }
  }

  // Get users with deduplication
  async getUsers(filters = {}) {
    try {
      const params = {
        page: filters.page || 1,
        limit: filters.limit || 50,
        ...filters
      };

      const response = await this.makeRequest('/api/admin/users', params);
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Create a new user
  async createUser(userData) {
    try {
      const response = await apiService.post('/api/admin/users', userData);
      // Clear users cache after creating a user
      this.clearCacheForEndpoint('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Update user details (email, full_name, roles)
  async updateUser(username, userData) {
    try {
      const response = await apiService.put(`/api/admin/users/${username}`, userData);
      // Clear users cache after updating a user
      this.clearCacheForEndpoint('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Update user status (enable/disable)
  async updateUserStatus(username, disabled) {
    try {
      const response = await apiService.put(`/api/admin/users/${username}/status`, null, {
        params: { disabled }
      });
      // Clear users cache after updating status
      this.clearCacheForEndpoint('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Update user roles
  async updateUserRoles(username, roles) {
    try {
      const response = await apiService.put(`/api/admin/users/${username}/roles`, roles);
      // Clear users cache after updating roles
      this.clearCacheForEndpoint('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error updating user roles:', error);
      throw error;
    }
  }

  // Delete a user
  async deleteUser(username) {
    try {
      const response = await apiService.delete(`/api/admin/users/${username}`);
      // Clear users cache after deleting a user
      this.clearCacheForEndpoint('/api/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Admin reset/update another user's password
  async resetUserPassword(username, newPassword) {
    try {
      const response = await apiService.put(`/api/admin/users/${username}/password`, {
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Error resetting user password:', error);
      throw error;
    }
  }

  // Change password
  async changePassword(passwordData) {
    try {
      const response = await apiService.post('/api/admin/users/me/password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  // Throttled realtime summary (reduce polling frequency)
  async getRealtimeSummary() {
    try {
      // Use longer cache for realtime data to reduce server load
      const cacheKey = 'realtime_summary';
      const cached = this.requestCache.get(cacheKey);
      const REALTIME_CACHE_TIMEOUT = 5000; // 5 seconds cache for realtime data
      
      if (cached && Date.now() - cached.timestamp < REALTIME_CACHE_TIMEOUT) {
        return cached.data;
      }

      const response = await apiService.get('/api/admin/realtime/summary');
      
      this.requestCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching realtime summary:', error);
      throw error;
    }
  }

   // Export feedback logs with all pages included
  async exportFeedback(params = {}) {
    try {
      // First try the server-side export endpoint if available
      try {
        // Add export flag to indicate we want all data
        const exportParams = { ...params, export: 'true' };
        
        const response = await apiService.get('/api/admin/feedback/export', { 
          params: exportParams,
          responseType: 'blob'
        });
        
        // Create a download link and trigger download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `feedback_logs_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        return { success: true };
      } catch (exportError) {
        console.log('Server-side export failed, using client-side export with pagination:', exportError);
      }
      
      // Client-side fallback: Fetch ALL feedback entries through pagination
      let allFeedback = [];
      let page = 1;
      let hasMoreData = true;
      const limit = 1000; // Use large page size for efficiency
      
      console.log('Starting client-side export with pagination');
      
      while (hasMoreData) {
        const paginationParams = {
          ...params,
          page: page,
          limit: limit
        };
        
        console.log(`Fetching page ${page} for export`);
        const response = await this.getFeedbackEntries(paginationParams);
        
        if (response && Array.isArray(response.feedback) && response.feedback.length > 0) {
          allFeedback = allFeedback.concat(response.feedback);
          console.log(`Page ${page}: Got ${response.feedback.length} entries, total so far: ${allFeedback.length}`);
          
          // Check if we have more pages
          const totalPages = Math.ceil(response.total / limit);
          if (page >= totalPages) {
            hasMoreData = false;
          } else {
            page++;
          }
        } else {
          hasMoreData = false;
          console.log(`Page ${page}: No more data available`);
        }
      }
      
      console.log(`Total feedback entries to export: ${allFeedback.length}`);
      
      if (allFeedback.length === 0) {
        throw new Error('No feedback data available to export');
      }
      
      // Create CSV content
      const csvHeaders = "timestamp,session_id,session_user,question,response,feedback_type,feedback_category,comments";
      const csvRows = allFeedback.map(entry => {
        // Escape CSV values properly
        const escapeCSV = (value) => {
          if (!value) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };
        
        return [
          escapeCSV(entry.timestamp || ''),
          escapeCSV(entry.session_id || ''),
        escapeCSV(entry.session_user || ''),
          escapeCSV(entry.question || ''),
          escapeCSV(entry.response || ''),
          escapeCSV(entry.feedback_type || 'Unspecified'),
          escapeCSV(entry.feedback_category || 'Unspecified'),
          escapeCSV(entry.comments || '')
        ].join(',');
      });
      
      const csvContent = csvHeaders + "\n" + csvRows.join("\n");
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `feedback_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error exporting feedback:', error);
      throw this.handleError(error);
    }
  }
  
  // Get data sources with pagination - Fixed implementation that properly handles tags
  async getDataSources(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Handle pagination
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);
      
      // Handle filters
      if (filters.source_type) queryParams.append('source_type', filters.source_type);
      if (filters.search) queryParams.append('search', filters.search);
      
      // Handle sorting
      if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
      if (filters.sort_direction) queryParams.append('sort_direction', filters.sort_direction);
      
      console.log("Fetching data sources with params:", Object.fromEntries(queryParams));
      
      // Use the backend API endpoint that provides document sources with tags
      let url = '/documents/available-sources';
      
      // Fetch data from API endpoint
      const response = await apiService.get(url, { params: Object.fromEntries(queryParams) });
      
      console.log("Data sources response:", response.data);
      
      // Process the response to ensure tags are properly formatted
      if (response.data && response.data.sources) {
        // Ensure each source has a tags array if it doesn't exist
        response.data.sources.forEach(source => {
          // Make sure tags is an array
          if (!source.tags) {
            source.tags = [];
          }
          // Convert string tags to array if needed
          else if (typeof source.tags === 'string') {
            try {
              source.tags = JSON.parse(source.tags);
            } catch (e) {
              source.tags = [source.tags]; // Fallback to single tag array if parsing fails
            }
          }
        });
      }
      
      // If API doesn't provide field mapping, add it here
      if (response.data && !response.data.field_mapping) {
        response.data.field_mapping = {
          filename: "filename", 
          type: "type",
          pages: "page_count",
          chunks: "chunk_count",
          tags: "tags"  // Added tags field mapping
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching data sources:', error);
      throw this.handleError(error);
    }
  }

  // Get details of a specific data source
  async getDataSourceDetails(sourceId) {
    try {
      const response = await apiService.get(`/api/admin/data-sources/${sourceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching data source details:', error);
      throw error;
    }
  }

  // Create a new data source
  async createDataSource(sourceData) {
    try {
      const response = await apiService.post('/api/admin/data-sources', sourceData);
      return response.data;
    } catch (error) {
      console.error('Error creating data source:', error);
      throw error;
    }
  }

  // Update an existing data source
  async updateDataSource(sourceId, sourceData) {
    try {
      const response = await apiService.put(`/api/admin/data-sources/${sourceId}`, sourceData);
      return response.data;
    } catch (error) {
      console.error('Error updating data source:', error);
      throw error;
    }
  }

  // Delete a data source
  async deleteDataSource(sourceId) {
    try {
      const response = await apiService.delete(`/api/admin/data-sources/${sourceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting data source:', error);
      throw error;
    }
  }

  // Refresh/reindex a data source
  async refreshDataSource(sourceId) {
    try {
      const response = await apiService.post(`/api/admin/data-sources/${sourceId}/refresh`);
      return response.data;
    } catch (error) {
      console.error('Error refreshing data source:', error);
      throw error;
    }
  }

  // Tag management methods for data sources
  async addTag(sourceId, tag) {
    try {
      const response = await apiService.post(`/api/admin/data-sources/${sourceId}/tags`, { tag });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async removeTag(sourceId, tag) {
    try {
      const response = await apiService.delete(`/api/admin/data-sources/${sourceId}/tags/${encodeURIComponent(tag)}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  async updateTags(sourceId, tags) {
    try {
      const response = await apiService.put(`/api/admin/data-sources/${sourceId}/tags`, { tags });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
  
  // Get alerts with deduplication
  async getAlerts(filters = {}) {
    try {
      const params = {
        limit: filters.limit || 100,
        ...filters
      };

      const response = await this.makeRequest('/api/admin/alerts', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      throw error;
    }
  }

  // Authentication methods (no caching for sensitive operations)
  async login(username, password) {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      
      const response = await apiService.post('/api/admin/auth/token', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw this.handleError(error);
    }
  }

  // Get current user with minimal caching
  async getCurrentUser() {
    try {
      const cacheKey = 'current_user';
      const cached = this.requestCache.get(cacheKey);
      const USER_CACHE_TIMEOUT = 30000; // 30 seconds cache for user info
      
      if (cached && Date.now() - cached.timestamp < USER_CACHE_TIMEOUT) {
        return cached.data;
      }

      const response = await apiService.get('/api/admin/users/me');
      
      this.requestCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }  }  // Export methods (no caching for downloads)
  async exportAuditLogs(params = {}) {
    try {
      console.log('Exporting audit logs with params:', params);
      
      const response = await apiService.get('/api/admin/audit-logs/export', { 
        params: {
          ...params,
          sort_by: 'timestamp',
          sort_order: 'desc'
        },
        responseType: 'blob'
      });
      
      // Check if the response is valid
      if (!response.data || response.data.size === 0) {
        console.error('Export returned empty data');
        throw new Error('Export returned empty data');
      }
      
      // Create a new blob with explicit type and charset for Excel compatibility
      // The BOM is already added on the server side
      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;'
      });
      
      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(blob);
      const filename = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      // Add to DOM, click and remove
      document.body.appendChild(link);
      link.click();
      
      // Clean up resources
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
      
      console.log(`CSV export successful: ${filename}`);
      return { success: true };
    } catch (error) {
      console.error('Error exporting logs:', error);
      throw this.handleError(error);
    }
  }

  // Clear cache when needed
  clearCache() {
    this.requestCache.clear();
    this.pendingRequests.clear();
  }

  // Clear specific cache entries
  clearCacheForEndpoint(endpoint) {
    const keysToDelete = [];
    for (const [key] of this.requestCache) {
      if (key.startsWith(endpoint)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.requestCache.delete(key));
  }

  // Logout - clear all cache
  logout() {
    localStorage.removeItem('adminToken');
    this.clearCache();
  }

  // Check authentication
  isAuthenticated() {
    return localStorage.getItem('adminToken') !== null;
  }

  getToken() {
    return localStorage.getItem('adminToken');
  }

  // Error handling
  handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.removeItem('adminToken');
        this.clearCache();
        window.location.href = '/admin/login';
      }
      return {
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        details: error.response.data
      };
    } else if (error.request) {
      return {
        message: 'No response received from server',
        details: error.request
      };
    } else {
      return {
        message: error.message,
        details: error
      };
    }
  }

  // Utility method to batch multiple requests
  async batchRequests(requests) {
    const results = await Promise.allSettled(requests.map(req => this.makeRequest(req.endpoint, req.params, req.options)));
    return results.map((result, index) => ({
      ...requests[index],
      status: result.status,
      data: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason : null
    }));
  }
  
  // ── Rate Limiting ─────────────────────────────────────────────

  async getRateLimitConfig() {
    try {
      const response = await apiService.get('/api/admin/rate-limit/config');
      return response.data;
    } catch (error) {
      console.error('Error fetching rate limit config:', error);
      throw this.handleError(error);
    }
  }

  async updateRateLimitConfig(config) {
    try {
      const response = await apiService.put('/api/admin/rate-limit/config', config);
      this.clearCacheForEndpoint('/api/admin/rate-limit/config');
      return response.data;
    } catch (error) {
      console.error('Error updating rate limit config:', error);
      throw this.handleError(error);
    }
  }

  async getApiKeyUsageStats() {
    try {
      const response = await apiService.get('/api/admin/rate-limit/usage');
      return response.data;
    } catch (error) {
      console.error('Error fetching rate limit usage stats:', error);
      throw this.handleError(error);
    }
  }

  async getApiAccessLogs(limit = 100, keyId = null) {
    try {
      let url = `/api/admin/rate-limit/access-logs?limit=${limit}`;
      if (keyId) url += `&key_id=${keyId}`;
      const response = await apiService.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching API access logs:', error);
      throw this.handleError(error);
    }
  }

  // Clear cache when needed
  clearCache() {
    this.requestCache.clear();
    this.pendingRequests.clear();
  }

  // Clear specific cache entries
  clearCacheForEndpoint(endpoint) {
    const keysToDelete = [];
    for (const [key] of this.requestCache) {
      if (key.startsWith(endpoint)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.requestCache.delete(key));
  }

  // Logout - clear all cache
  logout() {
    localStorage.removeItem('adminToken');
    this.clearCache();
  }

  // Check authentication
  isAuthenticated() {
    return localStorage.getItem('adminToken') !== null;
  }

  getToken() {
    return localStorage.getItem('adminToken');
  }

  // Error handling
  handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.removeItem('adminToken');
        this.clearCache();
        window.location.href = '/admin/login';
      }
      return {
        status: error.response.status,
        message: error.response.data.message || 'An error occurred',
        details: error.response.data
      };
    } else if (error.request) {
      return {
        message: 'No response received from server',
        details: error.request
      };
    } else {
      return {
        message: error.message,
        details: error
      };
    }
  }
  async resetRateLimitCounters() {
    try {
      const response = await apiService.post('/api/admin/rate-limit/reset', {});
      return response.data;
    } catch (error) {
      console.error('Error resetting rate limit counters:', error);
      throw error;
    }
  }
}

export default new AdminService();
